import { defineStore, storeToRefs } from 'pinia';

import type {
  ChatMutedGloballyData,
  ConversationDeleteData,
  ConversationMessageReactionEventData,
  ConversationMessageReadEventData,
  ConversationMutedData,
  ConversationParticipantRemoveData,
  ConversationPinnedData,
  ConversationReadData,
  ConversationResultDTO,
  ConversationUnreadData,
  DeviceLogoutData,
  MessageResultDTO,
} from '@nora-monorepo/nora-api-client';
import {
  EventPattern,
} from '@nora-monorepo/nora-api-client';
import type {
  SseEventHandler,
} from '@nora-monorepo/ui-shared';
import {
  checkIsMuted,
  hasAnyGroupRoleBeenUpdated,
  hasUserRoleBeenUpdated,
  playSound,
  useSseStore,
} from '@nora-monorepo/ui-shared';

import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';

export const useChatSseStore = defineStore('chatSseStore', () => {
  const sseStore = useSseStore();
  const userStore = useChatUserStore();
  const conversationStore = useConversationStore();
  const conversationMessageStore = useConversationMessageStore();

  const { isConversationMuted } = storeToRefs(conversationStore);

  async function startListening(): Promise<void> {
    await sseStore.startListening();
  }

  sseStore.addEventHandler(
    EventPattern.ConversationCreate,
    async (event) => {
      const data: unknown = event.eventData;
      const conversationData = data as ConversationResultDTO;

      await conversationStore.fetchAndAddConversation(conversationData.id);
    },
  );

  sseStore.addEventHandler(
    EventPattern.ConversationMessageCreate,
    (event) => {
      const data: unknown = event.eventData;
      const messageData = data as MessageResultDTO;

      const soundMuted = checkIsMuted(
        userStore.user?.chatMutedIndefinite,
        userStore.user?.chatMutedUntil,
      );

      if (
        messageData.sentByUserId !== userStore.user?.id
        && !isConversationMuted.value(messageData.conversationId)
      ) {
        if (!soundMuted) {
          playSound();
        }
      }

      conversationStore.addMessageToConversation(
        messageData.conversationId,
        messageData,
      );
    },
  );

  sseStore.addEventHandler(
    EventPattern.ConversationMessageUpdate,
    (event) => {
      const data: unknown = event.eventData;
      const messageData = data as MessageResultDTO;

      conversationMessageStore.updateMessage(messageData, messageData);
    },
  );

  sseStore.addEventHandler(
    EventPattern.ConversationMessageDelete,
    (event) => {
      const data: unknown = event.eventData;
      const messageData = data as MessageResultDTO;

      conversationStore.deleteMessageFromConversation(
        messageData.conversationId,
        messageData.id,
      );

      conversationStore.deleteQuotedMessagesFromConversation(
        messageData.conversationId,
        messageData.id,
      );
    },
  );

  sseStore.addEventHandler(
    EventPattern.ConversationUpdate,
    async (event) => {
      const data: unknown = event.eventData;
      const updatedConversationDTO = data as ConversationResultDTO;

      if (!userStore?.user?.id) {
        throw new Error(
          'No user object available when receiving conversation update',
        );
      }

      const currentConversation = conversationStore.getConversation(
        updatedConversationDTO.id,
      );

      if (!currentConversation) {
        await conversationStore.fetchAndAddConversation(
          updatedConversationDTO.id,
        );

        return;
      }

      const conversation = currentConversation.conversation;

      if (!conversation) {
        await conversationStore.fetchAndReplaceConversation(
          updatedConversationDTO.id,
        );

        return;
      }

      const userRoleUpdated = hasUserRoleBeenUpdated(
        conversation,
        updatedConversationDTO,
        userStore?.user?.id,
      );

      if (userRoleUpdated) {
        await conversationStore.fetchAndReplaceConversation(
          updatedConversationDTO.id,
        );

        return;
      }

      const currentUserInGroupOnly
        = currentConversation.conversation?.users.find(
          user => user.user.id === userStore.user?.id,
        ) ?? false;

      const anyGroupRoleBeenUpdated = hasAnyGroupRoleBeenUpdated(
        conversation,
        updatedConversationDTO,
      );

      if (currentUserInGroupOnly && anyGroupRoleBeenUpdated) {
        await conversationStore.fetchAndReplaceConversation(
          updatedConversationDTO.id,
        );

        return;
      }

      conversationStore.updateConversation(
        updatedConversationDTO.id,
        updatedConversationDTO,
      );
    },
  );

  sseStore.addEventHandler(EventPattern.ConversationDelete, (event) => {
    const data: unknown = event.eventData;
    const conversationDTO = data as ConversationDeleteData;

    conversationStore.deleteConversationFromState(conversationDTO.id);
  });

  sseStore.addEventHandler(
    EventPattern.ConversationParticipantRemove,
    (event) => {
      const data: unknown = event.eventData;
      const { conversationId } = data as ConversationParticipantRemoveData;
      conversationStore.deleteConversationFromState(conversationId);
    },
  );

  sseStore.addEventHandler(EventPattern.ConversationPinned, (event) => {
    const data: unknown = event.eventData;
    const pinned = data as ConversationPinnedData;

    if (pinned.pinned) {
      userStore.pinConversation(pinned.id);
    }
    else {
      userStore.unpinConversation(pinned.id);
    }
  });

  const handleReactionEvent: SseEventHandler = (event) => {
    const data: unknown = event.eventData;
    const { messageId, conversationId, sentByUserId }
      = data as ConversationMessageReactionEventData;

    if (sentByUserId === userStore.user?.id) {
      return;
    }

    const messageState = conversationStore.getConversationMessage(
      conversationId,
      messageId,
    );

    if (!messageState) {
      return;
    }

    const reactionEventData = data as ConversationMessageReactionEventData;

    conversationMessageStore.updateMessageReactions(
      messageState,
      reactionEventData.messageReactions,
    );
  };

  sseStore.addEventHandler(
    EventPattern.ConversationMessageReactionCreate,
    handleReactionEvent,
  );

  sseStore.addEventHandler(
    EventPattern.ConversationMessageReactionUpdate,
    handleReactionEvent,
  );

  sseStore.addEventHandler(
    EventPattern.ConversationMessageReactionDelete,
    handleReactionEvent,
  );

  sseStore.addEventHandler(
    EventPattern.ConversationMessageRead,
    (event) => {
      const data: unknown = event.eventData;
      const { messageId, conversationId, readMessagesCount }
        = data as ConversationMessageReadEventData;

      conversationMessageStore.addReadMessageStatus(
        conversationId,
        messageId,
        readMessagesCount,
      );
    },
  );

  sseStore.addEventHandler(EventPattern.ConversationRead, (event) => {
    const data: unknown = event.eventData;
    const { conversationId } = data as ConversationReadData;

    conversationStore.markConversationAsReadInState(
      conversationId,
      data as ConversationReadData,
    );
  });

  sseStore.addEventHandler(EventPattern.ConversationUnread, (event) => {
    const data: unknown = event.eventData;
    const { conversationId } = data as ConversationUnreadData;

    conversationStore.markConversationAsUnreadInState(conversationId);
  });

  sseStore.addEventHandler(EventPattern.DeviceLogout, (event) => {
    const data: unknown = event.eventData;
    const deviceLogoutData = data as DeviceLogoutData;

    if (deviceLogoutData.deviceId === localStorage.getItem('deviceId')) {
      sseStore.stopListening();
    }
  });

  sseStore.addEventHandler(EventPattern.GlobalMuteChat, (event) => {
    const data: unknown = event.eventData;
    const globalMuteData = data as ChatMutedGloballyData;
    userStore.setGlobalMute(
      {
        chatMutedUntil: globalMuteData.chatMutedUntil,
        chatMutedIndefinite: globalMuteData.chatMutedIndefinite,
      },
      true,
    );
  });

  sseStore.addEventHandler(EventPattern.ConversationMuted, async (event) => {
    const data: unknown = event.eventData;
    const mutedData = data as ConversationMutedData;
    await conversationStore.muteConversation(mutedData.conversationId, true);
  });

  sseStore.addEventHandler(
    EventPattern.ConversationUnMuted,
    async (event) => {
      const data: unknown = event.eventData;
      const unMutedData = data as ConversationMutedData;
      await conversationStore.unMuteConversation(
        unMutedData.conversationId,
        true,
      );
    },
  );

  return {
    startListening,
  };
});
